import React, { memo, useCallback } from 'react';
import classNames from 'classnames';
import {
  Box,
  FormHelperText,
  IconButton,
  InputLabel,
  TextField,
  makeStyles,
} from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import { Field } from 'react-final-form';
import { Add as AddIcon, RemoveOutlined } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  input: {
    fontSize: 24,
    fontWeight: 700,
  },
  label: {
    fontSize: 12,
    color: '#969AB2',
  },
  endAdornment: {
    display: 'flex',
    alignItems: 'center',
  },
  endAdornmentText: {
    fontSize: 12,
    fontWeight: 600,
    color: '#969AB2',
    marginRight: theme.spacing(0.5),
  },
  root: {
    '&.MuiInput-underline:before': {
      borderBottom: '1px solid #E1E1E1',
    },
    '&.MuiInput-underline:hover:before': {
      borderColor: theme.palette.primary.main,
    },
  },
  button: {
    height: 32,
    width: 32,
    backgroundColor: 'transparent',
    border: '1px solid #E1E1E1',
    borderRadius: 45,
    boxSizing: 'border-box',
    padding: 0,
    minWidth: 32,
    cursor: 'pointer',
  },
  removeButton: {
    margin: theme.spacing(0, 1),
  },
}));

interface NumberInputFieldWithActionsProps {
  name: string;
  label?: string;
  isInteger?: boolean;
  actionButtons?: boolean;
  customOnChange?: (value: string) => void;
  readOnly?: boolean;
  startAdornment?: React.ReactNode;
  endAdornmentText?: string;
  maxValue?: number;
  clampValue?: boolean;
  classes?: {
    input?: string;
    root?: string;
  };
  [key: string]: any;
}

const NumberInputFieldWithActions = ({
  name,
  label = '',
  isInteger = false,
  actionButtons = false,
  endAdornmentText,
  maxValue,
  clampValue = false,
  ...rest
}: NumberInputFieldWithActionsProps) => {
  const classes = useStyles();

  const formatValue = useCallback(
    (value: string) => {
      return !isInteger
        ? value.replace(/[^\d.]/g, '').replace(/\.(?=.*\.)/g, '')
        : value.replace(/[^\d]/g, '');
    },
    [isInteger]
  );

  const clampValueToMax = useCallback(
    (value: string) => {
      if (!clampValue || maxValue == null || value === '') {
        return value;
      }

      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) {
        return value;
      }

      return numericValue > maxValue ? String(maxValue) : value;
    },
    [clampValue, maxValue]
  );

  const normalizeValue = useCallback(
    (value: string) => clampValueToMax(formatValue(value)),
    [clampValueToMax, formatValue]
  );

  return (
    <Field
      name={name}
      render={({
        input: { name: inputName, onChange, value },
        meta: { touched, error, submitError, submitFailed, dirtySinceLastSubmit },
      }) => {
        // Use dirtySinceLastSubmit because it is present in current final-form types.
        const shouldShowSubmitError = submitFailed && submitError && !dirtySinceLastSubmit;
        const fieldError =
          touched && error ? error : shouldShowSubmitError ? submitError : undefined;
        const isError = Boolean(fieldError);

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          if (rest.customOnChange) {
            rest.customOnChange(normalizeValue(event.target.value));
          } else {
            onChange(normalizeValue(event.target.value));
          }
        };

        const updateValue = (nextValue: string) => {
          const formattedValue = normalizeValue(nextValue);
          if (rest.customOnChange) {
            rest.customOnChange(formattedValue);
          } else {
            onChange(formattedValue);
          }
        };

        const handleIncrementClick = () => {
          const baseValue = value ? Number(value) : 0;
          const calculatedValue = baseValue + 1;
          const nextValue =
            clampValue && maxValue != null ? Math.min(calculatedValue, maxValue) : calculatedValue;
          updateValue(String(nextValue));
        };

        const handleDecrementClick = () => {
          if (!value || Number(value) <= 0) {
            updateValue('0');
            return;
          }

          const calculatedValue = Math.max(Number(value) - 1, 0);
          updateValue(String(calculatedValue));
        };

        return (
          <Box>
            <InputLabel className={classes.label}>{label}</InputLabel>
            <TextField
              variant="standard"
              placeholder="0"
              value={value}
              onChange={handleChange}
              name={inputName}
              type="text"
              InputProps={{
                classes: {
                  input: classes.input,
                  root: classes.root,
                  ...(rest.classes ?? {}),
                },
                startAdornment: rest.startAdornment,
                ...((actionButtons || endAdornmentText) && {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box className={classes.endAdornment}>
                        {endAdornmentText && (
                          <span className={classes.endAdornmentText}>{endAdornmentText}</span>
                        )}
                        {actionButtons && (
                          <>
                            <IconButton
                              classes={{ root: classNames(classes.button, classes.removeButton) }}
                              onClick={handleDecrementClick}>
                              <RemoveOutlined />
                            </IconButton>
                            <IconButton
                              classes={{ root: classes.button }}
                              onClick={handleIncrementClick}>
                              <AddIcon />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </InputAdornment>
                  ),
                }),
                ...(rest.readOnly && {
                  readOnly: true,
                }),
              }}
              fullWidth
            />
            {isError && <FormHelperText error>{fieldError}</FormHelperText>}
          </Box>
        );
      }}
      {...rest}
    />
  );
};

export default memo(NumberInputFieldWithActions);
